import { useNavigate } from 'react-router-dom';
import { Video, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhotoThumbnail, usePhotoLightbox, getMediaDetailPath } from '@/components/ui/PhotoGallery';
import { useTranslation } from 'react-i18next';

/**
 * Rendu d'un champ vidéo avec lightbox (mode create : sans suppression)
 *
 * @param {function} [onVideoClick] - Remplace la lightbox au clic sur une vignette (ex. lecteur invité)
 */
export function VideoFieldCreate({ field, videos, onVideoCapture, isUploading, onVideoClick }) {
    const { openLightbox } = usePhotoLightbox(videos);
    const handleThumbnailClick = (video, index) => {
        if (onVideoClick) return onVideoClick(video, index);
        return openLightbox(index);
    };
    const minVideos = field.minVideos || field.options?.min || 1;
    const maxVideos = field.maxVideos || field.options?.max || 6;
    const canAddMore = videos.length < maxVideos;

    return (
        <div>
            <VideoFieldHeader field={field} videos={videos} minVideos={minVideos} maxVideos={maxVideos} />

            {videos.length > 0 && (
                <div className="flex flex-wrap gap-[12px] mb-[16px]">
                    {videos.map((video, i) => (
                        <PhotoThumbnail
                            key={video.uniq_id || i}
                            photo={video}
                            index={i}
                            showRemoveButton={false}
                            showHorodateBadge={true}
                            onClick={video.uniq_id ? () => handleThumbnailClick(video, i) : undefined}
                            width={200}
                            height={200}
                        />
                    ))}
                </div>
            )}

            {canAddMore && <VideoAddButton isUploading={isUploading} onClick={() => onVideoCapture(field.id)} />}
        </div>
    );
}

/**
 * Rendu d'un champ vidéo avec suppression et lien viewer (mode edit)
 */
export function VideoFieldEdit({ field, videos, onVideoCapture, onRemoveVideo, isUploading, sourceUrl }) {
    const navigate = useNavigate();
    const minVideos = field.minVideos || 1;
    const maxVideos = field.maxVideos || 6;
    const canAddMore = videos.length < maxVideos;

    const handleVideoClick = (video) => {
        // getMediaDetailPath détecte le type via mediaType/mime et route vers /videos/{id}
        const path = getMediaDetailPath(video);
        if (path) {
            navigate(path, { state: { from: sourceUrl } });
        }
    };

    return (
        <div>
            <VideoFieldHeader field={field} videos={videos} minVideos={minVideos} maxVideos={maxVideos} />

            {videos.length > 0 && (
                <div className="flex flex-wrap gap-[12px] mb-[16px]">
                    {videos.map((video, i) => (
                        <PhotoThumbnail
                            key={video.uniq_id || i}
                            photo={video}
                            index={i}
                            showRemoveButton={true}
                            onRemove={() => onRemoveVideo(field.id, i)}
                            showHorodateBadge={true}
                            onClick={video.uniq_id ? () => handleVideoClick(video) : undefined}
                            width={200}
                            height={200}
                        />
                    ))}
                </div>
            )}

            {canAddMore && <VideoAddButton isUploading={isUploading} onClick={() => onVideoCapture(field.id)} />}
        </div>
    );
}

function VideoFieldHeader({ field, videos, minVideos, maxVideos }) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center justify-between mb-[12px]">
            <div className="flex-1">
                <div className="flex items-center gap-[8px] mb-[4px]">
                    <h3 data-required={field.required || undefined} className="text-[15px] text-foreground">
                        {field.label}
                    </h3>
                    <span className="bg-secondary text-secondary-foreground rounded-[6px] px-[8px] py-[2px] text-[11px] font-medium">
                        {t('procedures:1JetonVideo')}
                    </span>
                </div>
                <p className="text-[12px] text-muted-foreground">
                    {t('procedures:minimum')} {minVideos} {t('procedures:video')}{minVideos > 1 ? 's' : ''}
                </p>
            </div>
            <span className="text-[13px] text-muted-foreground">
                {videos.length}/{maxVideos}
            </span>
        </div>
    );
}

function VideoAddButton({ isUploading, onClick }) {
    const { t } = useTranslation();
    return (
        <div>
            <Button
                type="button"
                variant="plain"
                color="secondary"
                size="cta"
                onClick={onClick}
                disabled={isUploading}
                className="w-full h-auto border-2 border-dashed border-secondary bg-secondary/5 rounded-[12px] py-[20px] transition-colors disabled:opacity-50 flex flex-col items-center"
            >
                {isUploading ? (
                    <Loader2 className="h-6 w-6 text-secondary mb-2 animate-spin" />
                ) : (
                    <Video className="h-6 w-6 text-secondary mb-2" />
                )}
                <p className="text-[14px] text-secondary">
                    {isUploading ? t('procedures:envoiEnCours') : t('procedures:enregistrerUneVideoHorodatee')}
                </p>
            </Button>
            <p className="text-[13px] text-muted-foreground text-center mt-[8px]">
                {t('procedures:importantChaqueEnregistrementConsommeraImmediate')}
            </p>
        </div>
    );
}
